// ============================================================
// raceEditor.ts — 3D level editor for marble race courses
// ============================================================

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import {
  Segment, SegmentType, LevelData, BodyConfig,
  PREFABS, SEGMENT_TYPES, createDefaultLevel,
  downloadLevel, uploadLevel, RENDER_FINISH,
  transformSegmentBodies,
} from './levelData';

const SNAP_POS = 0.5;
const SNAP_ROT = THREE.MathUtils.degToRad(15);

let nextId = 1;
function genId(): string { return `seg_${nextId++}`; }

export interface EditorAPI {
  getLevelData(): LevelData;
  loadLevel(data: LevelData): void;
  getSegmentCount(): number;
  destroy(): void;
}

interface EditorOptions {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  onBuildCourse: (data: LevelData) => void;
  onSegmentCountChange?: (count: number) => void;
}

export function createEditor(opts: EditorOptions): EditorAPI {
  const { scene, camera, renderer, controls, onBuildCourse } = opts;

  // State
  const level: LevelData = createDefaultLevel('My Course');
  const segmentGroups: Map<string, THREE.Group> = new Map();
  let selectedId: string | null = null;
  let isDestroyed = false;

  // Selection highlight ring
  const ringGeo = new THREE.RingGeometry(0.8, 1.0, 32);
  const ringMat = new THREE.MeshBasicMaterial({
    color: 0x44ff88,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.6,
    depthTest: false,
  });
  const selectionRing = new THREE.Mesh(ringGeo, ringMat);
  selectionRing.visible = false;
  selectionRing.rotation.x = -Math.PI / 2;
  scene.add(selectionRing);

  // TransformControls
  const transformControls = new TransformControls(camera, renderer.domElement);
  transformControls.setSize(0.6);
  transformControls.setTranslationSnap(SNAP_POS);
  transformControls.setRotationSnap(SNAP_ROT);
  transformControls.addEventListener('dragging-changed', (event) => {
    controls.enabled = !event.value;
  });
  transformControls.addEventListener('change', () => {
    updateFromTransform();
  });
  scene.add(transformControls.getHelper());

  // Raycaster for selection
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const clickables: THREE.Object3D[] = [];

  // Create mesh for a segment
  function addSegmentVisual(seg: Segment): THREE.Group {
    const prefab = PREFABS[seg.type];
    if (!prefab) {
      console.warn(`[Editor] Unknown segment type: ${seg.type}`);
      return new THREE.Group();
    }
    const group = prefab.createVisual(seg);
    group.position.set(seg.position[0], seg.position[1], seg.position[2]);
    group.rotation.set(seg.rotation[0], seg.rotation[1], seg.rotation[2]);
    group.scale.set(seg.scale?.[0] ?? 1, seg.scale?.[1] ?? 1, seg.scale?.[2] ?? 1);
    group.userData.segmentId = seg.id;
    scene.add(group);
    segmentGroups.set(seg.id, group);

    // Make all meshes in the group clickable
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.userData.segmentId = seg.id;
        clickables.push(child);
      }
    });

    return group;
  }

  function removeSegmentVisual(id: string) {
    const group = segmentGroups.get(id);
    if (group) {
      // Remove meshes from clickables
      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const idx = clickables.indexOf(child);
          if (idx >= 0) clickables.splice(idx, 1);
        }
      });
      scene.remove(group);
      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (Array.isArray(child.material)) {
            child.material.forEach(m => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      segmentGroups.delete(id);
    }
  }

  function updateFromTransform() {
    if (!selectedId) return;
    const seg = level.segments.find(s => s.id === selectedId);
    const group = segmentGroups.get(selectedId);
    if (!seg || !group) return;
    seg.position = [group.position.x, group.position.y, group.position.z];
    seg.rotation = [group.rotation.x, group.rotation.y, group.rotation.z];
    seg.scale = [group.scale.x, group.scale.y, group.scale.z];
    updateSelectionRing();
    opts.onSegmentCountChange?.(level.segments.length);
  }

  function updateSelectionRing() {
    if (!selectedId) {
      selectionRing.visible = false;
      return;
    }
    const group = segmentGroups.get(selectedId);
    if (!group) { selectionRing.visible = false; return; }

    // Compute bounding box of the group
    const box = new THREE.Box3().setFromObject(group);
    const center = new THREE.Vector3();
    box.getCenter(center);
    const size = new THREE.Vector3();
    box.getSize(size);
    const radius = Math.max(size.x, size.z) / 2 + 0.5;
    selectionRing.position.copy(center);
    selectionRing.position.y -= size.y / 2 + 0.05;
    selectionRing.scale.set(radius, radius, radius);
    selectionRing.visible = true;
  }

  // Pointer event handlers
  function onPointerDown(event: PointerEvent) {
    if (isDestroyed || transformControls.dragging) return;
    const rect = renderer.domElement.getBoundingClientRect();
    pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  }

  function onPointerUp(event: PointerEvent) {
    if (isDestroyed || transformControls.dragging) return;
    const rect = renderer.domElement.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const py = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Only treat as click if pointer barely moved
    if (Math.abs(px - pointer.x) > 0.01 || Math.abs(py - pointer.y) > 0.01) return;

    raycaster.setFromCamera(new THREE.Vector2(px, py), camera);
    const intersects = raycaster.intersectObjects(clickables, false);
    let hitId: string | null = null;
    if (intersects.length > 0) {
      let obj: THREE.Object3D | null = intersects[0].object;
      while (obj) {
        if (obj.userData.segmentId) {
          hitId = obj.userData.segmentId;
          break;
        }
        obj = obj.parent;
      }
    }

    if (hitId) {
      selectSegment(hitId);
    } else {
      deselectSegment();
    }
  }

  // Keyboard handler (Delete key + transform shortcuts + duplicate)
  function onKeyDown(event: KeyboardEvent) {
    if (isDestroyed) return;
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;

    if ((event.key === 'Delete' || event.key === 'Backspace') && selectedId) {
      deleteSelected();
    }
    if (event.key === 'Escape') {
      deselectSegment();
    }
    // Ctrl+D to duplicate
    if (event.key === 'd' && (event.ctrlKey || event.metaKey) && selectedId) {
      event.preventDefault();
      duplicateSelected();
    }

    // Transform mode shortcuts
    if (selectedId && !event.ctrlKey && !event.metaKey && !event.altKey) {
      if (event.key === 'w' || event.key === 'W') {
        event.preventDefault();
        setTransformMode('translate');
      } else if (event.key === 'e' || event.key === 'E') {
        event.preventDefault();
        setTransformMode('rotate');
      } else if (event.key === 'r' || event.key === 'R') {
        event.preventDefault();
        setTransformMode('scale');
      }
    }
  }

  function setTransformMode(mode: 'translate' | 'rotate' | 'scale') {
    transformControls.setMode(mode);
    const toolbar = document.getElementById('transform-toolbar');
    if (toolbar) {
      toolbar.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach(btn => {
        btn.classList.toggle('active-transform', btn.dataset.mode === mode);
      });
    }
  }

  function duplicateSelected() {
    if (!selectedId) return;
    const src = level.segments.find(s => s.id === selectedId);
    if (!src) return;
    const copy: Segment = {
      id: genId(),
      type: src.type,
      position: [src.position[0] + 1, src.position[1], src.position[2] + 1],
      rotation: [src.rotation[0], src.rotation[1], src.rotation[2]],
      scale: src.scale ? [...src.scale] : [1, 1, 1],
      properties: { ...src.properties },
    };
    level.segments.push(copy);
    addSegmentVisual(copy);
    selectSegment(copy.id);
    opts.onSegmentCountChange?.(level.segments.length);
  }

  function mirrorSelected() {
    if (!selectedId) return;
    const seg = level.segments.find(s => s.id === selectedId);
    if (!seg) return;
    // Mirror by negating Y rotation (reflects left/right orientation)
    seg.rotation[1] = -seg.rotation[1];
    updateGroupFromSeg(seg.id);
    updatePropertiesPanel(seg.id);
  }

  function selectSegment(id: string) {
    deselectSegment();
    selectedId = id;
    const group = segmentGroups.get(id);
    if (group) {
      transformControls.attach(group);
      // Highlight: make ring visible
      updateSelectionRing();
      // Update properties panel
      updatePropertiesPanel(id);
    }
  }

  function deselectSegment() {
    if (selectedId) {
      transformControls.detach();
      selectedId = null;
      selectionRing.visible = false;
      clearPropertiesPanel();
    }
  }

  function deleteSelected() {
    if (!selectedId) return;
    const id = selectedId;
    deselectSegment();
    removeSegmentVisual(id);
    const idx = level.segments.findIndex(s => s.id === id);
    if (idx >= 0) level.segments.splice(idx, 1);
    opts.onSegmentCountChange?.(level.segments.length);
  }

  // Add a segment
  function addSegment(type: SegmentType): Segment {
    const prefab = PREFABS[type];
    if (!prefab) throw new Error(`Unknown type: ${type}`);

    // Compute default position (end of last segment + 2 units in Z)
    let px = 0, py = 0, pz = 0;
    if (level.segments.length > 0) {
      const last = level.segments[level.segments.length - 1];
      const lastDepth = last.properties.depth ?? 4;
      pz = last.position[2] + lastDepth + 2;
      py = last.position[1];
    }

    const seg: Segment = {
      id: genId(),
      type,
      position: [px, py, pz],
      rotation: [0, 0, 0],
      scale: [1, 1, 1],
      properties: { ...prefab.defaultProperties },
    };
    level.segments.push(seg);
    addSegmentVisual(seg);
    selectSegment(seg.id);
    opts.onSegmentCountChange?.(level.segments.length);
    return seg;
  }

  // Clear all segments
  function clearAll() {
    deselectSegment();
    // Remove in reverse order
    const ids = [...segmentGroups.keys()];
    for (const id of ids) removeSegmentVisual(id);
    level.segments = [];
    opts.onSegmentCountChange?.(level.segments.length);
  }

  // Properties panel
  function updatePropertiesPanel(id: string) {
    const seg = level.segments.find(s => s.id === id);
    if (!seg) return;
    const container = document.getElementById('prop-content');
    if (!container) return;

    let html = `<div class="prop-label">${PREFABS[seg.type]?.label ?? seg.type}</div>`;

    html += '<div class="prop-section">Position</div>';
    html += createPropInput('pos-x', 'X', seg.position[0], (v) => { seg.position[0] = v; });
    html += createPropInput('pos-y', 'Y', seg.position[1], (v) => { seg.position[1] = v; });
    html += createPropInput('pos-z', 'Z', seg.position[2], (v) => { seg.position[2] = v; });

    html += '<div class="prop-section">Rotation (degrees)</div>';
    html += createPropInput('rot-x', 'X', THREE.MathUtils.radToDeg(seg.rotation[0]), (v) => { seg.rotation[0] = THREE.MathUtils.degToRad(v); });
    html += createPropInput('rot-y', 'Y', THREE.MathUtils.radToDeg(seg.rotation[1]), (v) => { seg.rotation[1] = THREE.MathUtils.degToRad(v); });
    html += createPropInput('rot-z', 'Z', THREE.MathUtils.radToDeg(seg.rotation[2]), (v) => { seg.rotation[2] = THREE.MathUtils.degToRad(v); });

    // Type-specific properties
    const prefab = PREFABS[seg.type];
    if (prefab) {
      const props = Object.keys(prefab.defaultProperties);
      if (props.length > 0) {
        html += '<div class="prop-section">Segment Properties</div>';
        for (const key of props) {
          const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
          html += createPropInput(`prop-${key}`, label, seg.properties[key] ?? prefab.defaultProperties[key], (v) => {
            seg.properties[key] = v;
            rebuildSegmentVisual(seg.id);
          });
        }
      }
    }

    html += `<div style="display:flex;gap:6px;margin-top:8px">
      <button class="editor-btn" style="flex:1" data-action="mirror-segment">🪞 Mirror</button>
      <button class="editor-btn" style="flex:1" data-action="duplicate-segment">📋 Duplicate</button>
      <button class="editor-btn delete-btn" style="flex:1" data-action="delete-segment">🗑 Delete</button>
    </div>`;
    container.innerHTML = html;

    // Wire up inputs
    container.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        const val = parseFloat(input.value);
        if (isNaN(val)) return;
        const action = input.dataset.action;
        if (!action) return;
        // Split action to find the property
        const [type, key] = action.split(':');
        if (type === 'pos') {
          const idx = ['x', 'y', 'z'].indexOf(key);
          if (idx >= 0) {
            seg.position[idx] = val;
            updateGroupFromSeg(seg.id);
          }
        } else if (type === 'rot') {
          const idx = ['x', 'y', 'z'].indexOf(key);
          if (idx >= 0) {
            seg.rotation[idx] = THREE.MathUtils.degToRad(val);
            updateGroupFromSeg(seg.id);
          }
        } else if (type === 'prop') {
          seg.properties[key] = val;
          rebuildSegmentVisual(seg.id);
        }
      });
    });

    // Wire up delete button and duplicate button
    const delBtn = container.querySelector('[data-action="delete-segment"]');
    if (delBtn) {
      delBtn.addEventListener('click', () => deleteSelected());
    }
    const dupBtn = container.querySelector('[data-action="duplicate-segment"]');
    if (dupBtn) {
      dupBtn.addEventListener('click', () => duplicateSelected());
    }
    const mirBtn = container.querySelector('[data-action="mirror-segment"]');
    if (mirBtn) {
      mirBtn.addEventListener('click', () => mirrorSelected());
    }
  }

  function clearPropertiesPanel() {
    const container = document.getElementById('prop-content');
    if (container) {
      container.innerHTML = '<p class="hint">Select a segment to edit</p>';
    }
  }

  function createPropInput(action: string, label: string, value: number, onSet?: (v: number) => void): string {
    return `<div class="prop-row">
      <label>${label}</label>
      <input type="number" step="0.5" value="${value.toFixed(2)}" data-action="${action}">
    </div>`;
  }

  function updateGroupFromSeg(id: string) {
    const seg = level.segments.find(s => s.id === id);
    const group = segmentGroups.get(id);
    if (!seg || !group) return;
    group.position.set(seg.position[0], seg.position[1], seg.position[2]);
    group.rotation.set(seg.rotation[0], seg.rotation[1], seg.rotation[2]);
    group.scale.set(seg.scale?.[0] ?? 1, seg.scale?.[1] ?? 1, seg.scale?.[2] ?? 1);
    if (selectedId === id) {
      updateSelectionRing();
    }
  }

  function rebuildSegmentVisual(id: string) {
    const seg = level.segments.find(s => s.id === id);
    if (!seg) return;
    removeSegmentVisual(id);
    addSegmentVisual(seg);
    if (selectedId === id) {
      const group = segmentGroups.get(id);
      if (group) transformControls.attach(group);
    }
  }

  // Palette panel
  function buildPalette() {
    const container = document.getElementById('palette-list');
    if (!container) return;
    let html = '';
    for (const type of SEGMENT_TYPES) {
      const prefab = PREFABS[type];
      if (!prefab) continue;
      html += `<button class="palette-item" data-type="${type}" style="--seg-color:${prefab.color}">
        <span class="palette-swatch" style="background:${prefab.color}"></span>
        <span class="palette-label">${prefab.label}</span>
      </button>`;
    }
    container.innerHTML = html;

    container.querySelectorAll('.palette-item').forEach(btn => {
      btn.addEventListener('click', () => {
        const type = (btn as HTMLElement).dataset.type as SegmentType;
        if (type) addSegment(type);
      });
    });
  }

  function buildTransformToolbar() {
    const toolbar = document.getElementById('transform-toolbar');
    if (!toolbar) return;
    const buttons = toolbar.querySelectorAll<HTMLButtonElement>('[data-mode]');
    buttons.forEach(btn => {
      btn.onclick = () => {
        const mode = btn.dataset.mode as 'translate' | 'rotate' | 'scale';
        if (mode) setTransformMode(mode);
      };
    });
    setTransformMode('translate');
  }

  // === BUILD COURSE (for PLAY mode) ===
  // Returns body configs and start platform info
  function buildCourseConfigs(): { bodies: BodyConfig[], startInfo: { x: number, y: number, z: number, halfDepthZ: number } | null } {
    const bodies: BodyConfig[] = [];
    let startInfo: { x: number, y: number, z: number, halfDepthZ: number } | null = null;

    for (const seg of level.segments) {
      const prefab = PREFABS[seg.type];
      if (!prefab) continue;
      let segBodies = prefab.createBodyConfigs(seg);
      segBodies = transformSegmentBodies(segBodies, seg);
      bodies.push(...segBodies);

      if (seg.type === 'start_platform') {
        startInfo = {
          x: seg.position[0],
          y: seg.position[1],
          z: seg.position[2],
          halfDepthZ: seg.properties.depth / 2,
        };
      }
    }

    return { bodies, startInfo };
  }

  // === BUILD LEVEL ===
  function buildLevel(levelData: LevelData) {
    clearAll();
    for (const seg of levelData.segments) {
      level.segments.push(seg);
      addSegmentVisual(seg);
    }
    opts.onSegmentCountChange?.(level.segments.length);
  }

  // === INIT ===
  function init() {
    buildPalette();
    buildTransformToolbar();

    // Add event listeners
    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointerup', onPointerUp);
    document.addEventListener('keydown', onKeyDown);

    // Wire up editor buttons
    document.getElementById('btn-save-level')?.addEventListener('click', () => {
      downloadLevel(level);
    });
    document.getElementById('btn-load-level')?.addEventListener('click', async () => {
      try {
        const data = await uploadLevel();
        buildLevel(data);
      } catch (e) {
        console.warn('[Editor] Load cancelled or failed:', e);
      }
    });
    document.getElementById('btn-clear-level')?.addEventListener('click', () => {
      if (level.segments.length === 0) return;
      if (confirm('Clear all segments?')) clearAll();
    });
    document.getElementById('btn-build-level')?.addEventListener('click', () => {
      opts.onBuildCourse(level);
    });
  }

  // === EDITOR API ===
  function getLevelData(): LevelData {
    return level;
  }

  function getSegmentCount(): number {
    return level.segments.length;
  }

  function destroy() {
    isDestroyed = true;
    deselectSegment();
    clearAll();
    renderer.domElement.removeEventListener('pointerdown', onPointerDown);
    renderer.domElement.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('keydown', onKeyDown);
    scene.remove(selectionRing);
    selectionRing.geometry.dispose();
    selectionRing.material.dispose();
    scene.remove(transformControls.getHelper());
    transformControls.dispose();
  }

  init();

  return { getLevelData, loadLevel: buildLevel, getSegmentCount, destroy };
}
